/**
 * Widget Definition Types
 *
 * The `defineWidget()` pattern co-locates component metadata, prop schema,
 * slot definitions, data dependencies, and defaults in a single source of truth.
 * Used by the Page Customizer editor (Epic 02+) to render the widget library,
 * config panels, and default section config on add.
 *
 * Composition model: WidgetDefinition wraps SectionTypeDefinition (not extends).
 */

import type { SectionTypeDefinition } from './types';
import type { SectionEnvelope } from '../page-config/types';
import type { BlockNode, BlockGroupConfig } from './block-types';

// ═══════════════════════════════════════════════════════════════════════════
// WIDGET CATEGORIES
// ═══════════════════════════════════════════════════════════════════════════

export const WIDGET_CATEGORIES = ['content', 'products', 'navigation', 'layout', 'templates'] as const;
export type WidgetCategory = (typeof WIDGET_CATEGORIES)[number];

// ═══════════════════════════════════════════════════════════════════════════
// WIDGET DEFINITION (composition over inheritance)
// ═══════════════════════════════════════════════════════════════════════════

export interface WidgetDefinition {
  /** Section type key (e.g., 'hero', 'product-grid') */
  type: string;
  /** The underlying section type schema (props, slots, dataDeps) */
  schema: SectionTypeDefinition;
  /** Thai label for the widget library */
  nameTh: string;
  /** Thai description */
  descriptionTh: string;
  /** Category for grouping in the widget library */
  category: WidgetCategory;
  /** Thumbnail URL (relative to storefront public/) or base64 data URI */
  thumbnail: string;
  /** Default SectionEnvelope when user adds this widget — shape: { type, props, slots? } */
  defaults: SectionEnvelope;
  /** Optional default blocks for block-enabled widgets. When present, the widget supports block-level editing. */
  defaultBlocks?: BlockNode[];
  /** Group configuration for multi-group widgets. When present, blocks are
   *  rendered per-group by GenericGroupedBlockLayout instead of flat. */
  blockGroups?: BlockGroupConfig[];
}

/**
 * Typed identity function for widget definitions.
 * Provides IntelliSense and type checking with zero runtime overhead.
 *
 * @example
 * export const heroWidget = defineWidget({
 *   type: 'hero',
 *   schema: { label: 'Hero Banner', ... },
 *   nameTh: 'แบนเนอร์หลัก',
 *   ...
 * });
 */
export function defineWidget(def: WidgetDefinition): WidgetDefinition {
  return def;
}

// ═══════════════════════════════════════════════════════════════════════════
// CATALOG ITEM (lightweight shape for API response)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Shape returned by /api/widget-catalog.
 * Omits `defaults` to keep the response lightweight —
 * defaults are fetched from WIDGET_REGISTRY on the client when adding a section.
 */
export interface WidgetCatalogItem {
  type: string;
  name: string;
  nameTh: string;
  description: string;
  descriptionTh: string;
  category: WidgetCategory;
  thumbnail: string;
  props: SectionTypeDefinition['props'];
  slots: SectionTypeDefinition['slots'];
  dataDeps: string[];
}
